import { join } from "node:path";
// Type-only — erased at compile time so the optional `memory-extended` add-on
// (and its native sqlite/level deps) is NOT pulled into the module graph of a
// base install. The runtime engine is loaded lazily inside the recall closure.
import type { vector } from "@vortex-os/memory-extended";
import { AmbientRecaller } from "@vortex-os/proactive-curator";
import type { ModuleContext } from "@vortex-os/core";

/**
 * Bridge the `@vortex-os/memory-extended` recall engine into a
 * `@vortex-os/proactive-curator` {@link AmbientRecaller} — defined in the
 * plugin, exactly where `/recall` and `/curate` are defined over their
 * respective engines. This keeps both modules free of a dependency on each
 * other; the plugin is the only place that knows about both.
 *
 * This is deliberately **not** a slash command. Ambient recall is stateful
 * across turns (its dedup set + backpressure streak), so it belongs to a
 * long-lived host — the opt-in embedder daemon / `UserPromptSubmit` hook, or
 * any host runtime that keeps one instance alive for the session. The default
 * Claude Code surface is agent-guidance (see `docs/proactive-curator-design.md`);
 * this factory is for hosts that opt into the *automatic* surface.
 *
 * Construct **once per session** and reuse the returned recaller so its gate
 * state persists. The injected `RecallFn` opens DB handles lazily per call
 * and closes them in a `finally`, so a long-lived process never holds a file
 * lock between turns. The embedder (the expensive, warm-able part) is
 * supplied by the host so it can keep the model resident across calls.
 */
export interface AmbientRecallFactoryOptions {
  /** Embedding function. The host keeps this warm (daemon) to avoid per-call model loads. */
  readonly embed: vector.EmbedFn;
  /** Override the DB path. Default `<dataDir>/_indexes/memory.sqlite`. */
  readonly dbPath?: (ctx: ModuleContext) => string;
  /** Restrict recall to one corpus. Omit to search all. */
  readonly source?: vector.VectorSource;
  /** Forwarded to AmbientRecaller — minimum cosine score to surface. Default 0.5. */
  readonly minScore?: number;
  /** Forwarded to AmbientRecaller — max suggestions per consider(). Default 1. */
  readonly maxSuggestions?: number;
  /** Forwarded to AmbientRecaller — skip the engine below this query length. Default 12. */
  readonly minQueryChars?: number;
}

function defaultDbPath(ctx: ModuleContext): string {
  return join(ctx.dataDir, "_indexes", "memory.sqlite");
}

/**
 * Build a session-scoped {@link AmbientRecaller} wired to the recall engine.
 * Returns `undefined`-free — the caller owns the lifecycle.
 */
export function createAmbientRecaller(
  ctx: ModuleContext,
  options: AmbientRecallFactoryOptions,
): AmbientRecaller {
  const resolveDb = options.dbPath ?? defaultDbPath;
  const dbPath = resolveDb(ctx);

  return new AmbientRecaller({
    ...(options.minScore !== undefined ? { minScore: options.minScore } : {}),
    ...(options.maxSuggestions !== undefined ? { maxSuggestions: options.maxSuggestions } : {}),
    ...(options.minQueryChars !== undefined ? { minQueryChars: options.minQueryChars } : {}),
    recall: async (query, opts) => {
      // Lazy-load the optional add-on per call. Base ships without it; this
      // resolves only when `memory-extended` is installed alongside base.
      const { sqlite, vector, recall: recallEngine } = await import(
        "@vortex-os/memory-extended"
      );
      const sqlStore = new sqlite.MemorySqliteStore(dbPath);
      const vecStore = new vector.MemoryVectorStore({ db: dbPath });
      const chunkStore = new vector.SessionChunkStore(dbPath);
      try {
        const result = await recallEngine.recall(
          {
            query,
            // Ambient recall stays SEMANTIC-only this batch (§12 R7). The
            // AmbientRecaller gates hits by a cosine `minScore`, which is only
            // meaningful for cosine scores — a keyword-only hit carries a
            // rank-confidence score that the cosine threshold would mis-gate.
            // Keyword/hybrid are exposed via the explicit `/recall` command + MCP.
            mode: "semantic",
            ...(opts?.k !== undefined ? { k: opts.k } : {}),
            ...(options.source !== undefined ? { source: options.source } : {}),
          },
          { sqlite: sqlStore, vector: vecStore, embed: options.embed, sessionChunks: chunkStore },
        );
        // RecallResult.hits structurally satisfies readonly AmbientRecallHit[].
        return { hits: result.hits };
      } finally {
        chunkStore.close();
        vecStore.close();
        sqlStore.close();
      }
    },
  });
}
